import React from "react";
import { Code2, Download } from "lucide-react";
import { Button } from "@magicbi/ui-library";
import { useStoryItemSql } from "../../../hooks/useStoryItemSql";
import { SoonBadge } from "../../atoms/SoonBadge";
import { Checkbox } from "../../atoms/Checkbox";
import { DialogShell } from "../../molecules/DialogShell";
import { SegmentedPicker } from "../../molecules/SegmentedPicker";
import { rawColors } from "../../../design-system/tokens/colors";
import { storySqlDialogCopy as copy } from "./StorySqlDialog.copy";
import {
  ChartList,
  ChartRow,
  CompareRow,
  Detail,
  DisabledBlock,
  GroupHead,
  GroupTitle,
  KindChip,
  KindIcon,
  SecurityChip,
  SecurityLabel,
  TextRow,
  TopRow,
  TopTitle,
  Layout,
  ListPane,
  ListTop,
  Muted,
  Note,
  Panes,
  RowKind,
  RowTitle,
  SearchInput,
  Spacer,
} from "./StorySqlDialog.styles";
import type { StorySqlDialogProps } from "./StorySqlDialog.types";
import type { StorySqlGroupMode } from "../../../services/storyCanvas/storySqlGroups";
import { storySqlKindStyle } from "./storySqlKinds";
import { StorySqlEnginePane } from "./StorySqlEnginePane";
import { STORY_SQL_SAMPLE_MASKED_COLUMN_COUNT, STORY_SQL_SAMPLE_PARAMETERS } from "./storySqlSecurity.mock";
import { StorySqlPane } from "./StorySqlPane";
import { useStorySqlDialogInternalState } from "./useStorySqlDialogInternalState";

const SoonTag: React.FC = () => <SoonBadge label={copy.soon} />;

/**
 * The SQL behind the whole story: every chart on the left, the picked chart's readable SQL on the right,
 * or two charts side by side. Engine SQL, per-page grouping and the security strip are disabled teasers.
 */
export const StorySqlDialog: React.FC<StorySqlDialogProps> = ({
  open,
  storyId,
  storyTitle,
  charts,
  textItems = [],
  initialChartId,
  pages,
  onClose,
}) => {
  const state = useStorySqlDialogInternalState(
    storyId,
    storyTitle,
    open,
    charts,
    initialChartId,
    onClose,
    pages,
    copy.noTag,
  );
  const byId = new Map(charts.map((chart) => [chart.id, chart]));
  const needle = state.query.trim().toLowerCase();
  const visibleTextItems = needle ? textItems.filter((t) => t.label.toLowerCase().includes(needle)) : textItems;
  const isSingle = state.shownIds.length === 1;
  const shown = isSingle ? byId.get(state.shownIds[0]) : undefined;
  // Shares the readable pane's query (same key), so this adds no request; names the model in the header.
  const shownSql = useStoryItemSql(storyId, shown?.id ?? null, open && isSingle);
  const showReadable = state.layout !== "engine";
  // Engine SQL shows for one chart at a time; a comparison is Readable only.
  const showEngine = isSingle && open && state.layout !== "readable";
  return (
    <DialogShell
      open={open}
      size="xl"
      chrome="design"
      flush
      title={copy.title}
      titleId="story-sql-dialog-title"
      description={[storyTitle, copy.tiles(charts.length), shownSql.data?.modelName ? copy.modelSuffix(shownSql.data.modelName) : null]
        .filter(Boolean)
        .join(" · ")}
      descriptionId="story-sql-dialog-description"
      closeLabel={copy.close}
      dialogRef={state.dialog.dialogRef}
      titleRef={state.dialog.titleRef}
      onKeyDown={state.dialog.onKeyDown}
      onClose={onClose}
      headerEnd={
        <>
          {state.downloadState === "error" ? <Muted role="alert">{copy.downloadFailed}</Muted> : null}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={charts.length === 0 || state.downloadState === "busy"}
            onClick={() => void state.downloadAll()}
          >
            <Download size={14} aria-hidden="true" />
            {state.downloadState === "busy" ? copy.downloading : copy.downloadAll}
          </Button>
        </>
      }
    >
      <Layout>
        <ListPane aria-label={copy.listLabel}>
          <ListTop>
            <SearchInput
              type="search"
              aria-label={copy.searchLabel}
              placeholder={copy.searchPlaceholder}
              value={state.query}
              onChange={(e) => state.setQuery(e.target.value)}
            />
            <CompareRow>
              <Checkbox
                checked={state.isComparing}
                onChange={state.toggleCompare}
                ariaLabel={copy.compare}
              />
              {copy.compare}
              <Spacer />
              {copy.withSql(charts.length)}
            </CompareRow>
          </ListTop>
          <GroupHead>
            <SegmentedPicker
              variant="compact"
              ariaLabel={copy.groupLabel}
              value={state.groupMode}
              onChange={(id) => state.setGroupMode(id as StorySqlGroupMode)}
              options={[
                { id: "all", label: copy.allCharts },
                {
                  id: "page",
                  label: copy.groupByPage,
                  disabled: !state.canGroupByPage,
                  ariaLabel: state.canGroupByPage ? undefined : `${copy.groupByPage}: ${copy.needsPages}`,
                },
                {
                  id: "tag",
                  label: copy.groupByTag,
                  disabled: !state.canGroupByTag,
                  ariaLabel: state.canGroupByTag ? undefined : `${copy.groupByTag}: ${copy.needsTags}`,
                },
              ]}
            />
          </GroupHead>
          <ChartList>
            {state.groups.map((group) => (
              <React.Fragment key={group.key}>
                {group.title !== null ? <GroupTitle>{group.title}</GroupTitle> : null}
            {group.items.map((chart) => {
              const active = state.isComparing
                ? state.comparedIds.includes(chart.id)
                : chart.id === state.activeId;
              const look = storySqlKindStyle(chart.kind);
              const Icon = look.icon;
              return (
                <li key={chart.id}>
                  <ChartRow
                    type="button"
                    $active={active}
                    aria-pressed={active}
                    onClick={() => state.pick(chart.id)}
                  >
                    <KindIcon
                      $bg={active ? rawColors.primary[500] : look.bg}
                      $fg={active ? rawColors.text.white : look.fg}
                    >
                      <Icon size={13} aria-hidden="true" />
                    </KindIcon>
                    <RowTitle>{chart.label}</RowTitle>
                    {chart.kind ? <RowKind>{chart.kind}</RowKind> : null}
                  </ChartRow>
                </li>
              );
            })}
              </React.Fragment>
            ))}
            {visibleTextItems.map((item) => (
              <li key={item.id}>
                <TextRow aria-disabled="true">
                  <KindIcon $bg={rawColors.structural[100]} $fg={rawColors.text.subtleEmphasis}>
                    <Code2 size={13} aria-hidden="true" />
                  </KindIcon>
                  <RowTitle>{item.label}</RowTitle>
                  <RowKind>{copy.noQuery}</RowKind>
                </TextRow>
              </li>
            ))}
          </ChartList>
        </ListPane>
        <Detail>
          {charts.length === 0 ? <Note>{copy.noCharts}</Note> : null}
          {state.isComparing && state.comparedIds.length < 2 ? (
            <Note>{copy.comparePick}</Note>
          ) : null}
          <TopRow>
            {shown ? (
              <>
                <TopTitle>{shown.label}</TopTitle>
                {shown.kind ? <KindChip>{shown.kind}</KindChip> : null}
              </>
            ) : null}
            <Spacer />
                        <SegmentedPicker
              variant="inline"
              ariaLabel={copy.layoutLabel}
              value={state.layout}
              onChange={(id) => state.setLayout(id === "readable" || id === "engine" ? id : "both")}
              options={[
                { id: "both", label: copy.both },
                { id: "readable", label: copy.readable },
                { id: "engine", label: copy.engine },
              ]}
            />
          </TopRow>
          <Panes $columns={state.shownIds.length > 1 ? 2 : 1}>
            {(showReadable ? state.shownIds : []).map((id) => (
              <StorySqlPane
                key={id}
                storyId={storyId}
                chartId={id}
                title={byId.get(id)?.label ?? ""}
                kind={byId.get(id)?.kind}
                showHead={!isSingle}
                copiedKey={state.copiedKey}
                onCopy={state.onCopy}
              />
            ))}
          </Panes>
          {showEngine && shown ? (
            <StorySqlEnginePane
              storyId={storyId}
              chartId={shown.id}
              copiedKey={state.copiedKey}
              onCopy={state.onCopy}
            />
          ) : null}
          {charts.length > 0 ? <Note>{copy.note}</Note> : null}
          {/* PENDING: PENDING-SECURITY-INFO */}
          <DisabledBlock aria-disabled="true">
            <SoonTag />
            <SecurityLabel>{copy.parameters}</SecurityLabel>
            {STORY_SQL_SAMPLE_PARAMETERS.map((parameter) => (
              <SecurityChip key={parameter.name} $tone="params">
                {copy.parameterChip(parameter.name, parameter.value)}
              </SecurityChip>
            ))}
            <SecurityChip $tone="rls">{copy.rowLevelSecurityApplied}</SecurityChip>
            <SecurityChip $tone="masked">{copy.maskedColumnsExcluded(STORY_SQL_SAMPLE_MASKED_COLUMN_COUNT)}</SecurityChip>
          </DisabledBlock>
        </Detail>
      </Layout>
    </DialogShell>
  );
};

export default StorySqlDialog;
